import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StaticMap } from '@/components/ui/StaticMap';
import { cn } from '@/lib/utils';
import { Position } from '@certhon/models';
import { ChevronDown } from 'lucide-react';

interface LocationMapBoxProps {
  position: Position;
  label: string;
  className?: string;
  showMapProviders?: boolean;
  showCoordinates?: boolean;
  minHeight?: string;
}

export function LocationMapBox({
  position,
  label,
  className,
  showMapProviders = true,
  showCoordinates = true,
  minHeight = 'min-h-[200px]',
}: LocationMapBoxProps) {
  const formatCoordinate = (coord: number) => coord.toFixed(6);
  const formatAccuracy = (accuracy: number | null) =>
    accuracy ? `±${Math.round(accuracy)}m` : 'Unknown';

  const openInMap = (provider: 'osm' | 'google' | 'apple' | 'bing') => {
    const { latitude, longitude } = position.coords;
    let url: string;

    switch (provider) {
      case 'osm':
        url = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=16#map=16/${latitude}/${longitude}`;
        break;
      case 'google':
        url = `https://maps.google.com/maps?q=${latitude},${longitude}`;
        break;
      case 'apple':
        url = `http://maps.apple.com/?q=${latitude},${longitude}`;
        break;
      case 'bing':
        url = `https://www.bing.com/maps?q=${latitude},${longitude}`;
        break;
    }
    window.open(url, '_blank');
  };

  return (
    <div className={cn('relative', minHeight, className)}>
      {/* Map background - covers entire container */}
      <div className="absolute inset-0">
        <StaticMap
          latitude={position.coords.latitude}
          longitude={position.coords.longitude}
          zoom={16}
          className="h-full w-full rounded-lg"
        />
      </div>

      {/* Content overlay with backdrop */}
      <div
        className="absolute inset-0 z-10 flex flex-col justify-between rounded-lg bg-black/20 p-3
          text-white"
      >
        {/* Header with title and optional maps dropdown */}
        <div className="flex items-start justify-between">
          <p
            className="text-sm font-medium text-white"
            style={{ textShadow: '0 0 10px rgba(0,0,0,0.9)' }}
          >
            {label}
          </p>
          {showMapProviders && (
            <div className="flex">
              {/* Primary action - Open in OpenStreetMap */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openInMap('osm')}
                className="h-6 rounded-r-none border-r-0 border-white/30 bg-black/30 px-2 text-xs
                  text-white drop-shadow-lg hover:bg-black/40"
              >
                Open in Maps
              </Button>

              {/* Secondary action - Dropdown for other map providers */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-6 rounded-l-none border-white/30 border-l-white/30 bg-black/30 px-1 text-xs
                      text-white drop-shadow-lg hover:bg-black/40"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => openInMap('osm')}>
                    OpenStreetMap
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openInMap('google')}>
                    Google Maps
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openInMap('apple')}>
                    Apple Maps
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openInMap('bing')}>
                    Bing Maps
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Footer with optional condensed coordinates */}
        {showCoordinates && (
          <div
            className="text-xs text-white"
            style={{ textShadow: '0 0 10px rgba(0,0,0,0.9)' }}
          >
            <div className="flex items-center justify-start gap-2">
              <span>
                {formatCoordinate(position.coords.latitude)},{' '}
                {formatCoordinate(position.coords.longitude)}
              </span>
              <span
                className={cn(
                  'font-medium',
                  position.coords.accuracy && position.coords.accuracy < 5
                    ? 'text-green-300'
                    : position.coords.accuracy && position.coords.accuracy < 10
                      ? 'text-yellow-300'
                      : position.coords.accuracy &&
                          position.coords.accuracy < 50
                        ? 'text-orange-300'
                        : 'text-red-300',
                )}
              >
                {formatAccuracy(position.coords.accuracy)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
